import React, { useCallback, useEffect, useState } from "react";
import apiClient from "../../../../api/apiClient";
import { useToast } from "../../../../context/toastContext";
import PartnerSummary from "../../../../components/SupplyChainPartners/PartnerSummary";

/**
 * Admin (read-mostly) view of one license's supply chain: its vendors and customers, the
 * businesses that list it (including unverified and disputed listings, which the license
 * itself never sees), and CRM leads that may BE this license, with a Link action.
 * Partners are edited only by the license's owner/admin/manager in Settings.
 */
export default function AdminSupplyChainPanel({ licenseNo }) {
    const toast = useToast();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [linking, setLinking] = useState(null);

    const load = useCallback(() => {
        setError(false);
        apiClient
            .get(`/admin/licenses/${encodeURIComponent(licenseNo)}/supply-chain`)
            .then((res) => setData(res.data))
            .catch(() => setError(true));
    }, [licenseNo]);

    useEffect(() => {
        load();
    }, [load]);

    const linkLead = async (lead) => {
        setLinking(lead.id);
        try {
            await apiClient.post(`/admin/licenses/${encodeURIComponent(licenseNo)}/link-lead`, { leadId: lead.id });
            toast.success(`CRM lead "${lead.company}" linked to this license.`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to link lead.");
        } finally {
            setLinking(null);
        }
    };

    if (error) return <div className="admin-license-users-loading">Couldn&apos;t load the supply chain.</div>;
    if (!data) return <div className="admin-license-users-loading">Loading supply chain…</div>;

    const suggestions = [...(data.leadSuggestions?.strong || []), ...(data.leadSuggestions?.name || [])];

    return (
        <div className="admin-license-vendors-panel" style={{ marginTop: "var(--space-4)" }}>
            <h4 style={{ margin: "0 0 var(--space-2)" }}>Supply Chain</h4>
            <PartnerSummary profile={data.businessType} partners={data.partners} operationType={data.operationType} />

            <h5 style={{ margin: "var(--space-3) 0 var(--space-1)" }}>Listed by</h5>
            {data.listedBy.length === 0 ? (
                <div className="admin-license-users-loading">No business lists this license.</div>
            ) : (
                data.listedBy.map((l) => (
                    <div key={l.id} className="admin-license-user-chip">
                        <span className="admin-license-user-name">{l.reporterCompany || l.reporterLicenseNo}</span>
                        <span className="admin-license-meta" style={{ display: "inline" }}>
                            lists it as a {l.relation}
                        </span>
                        {l.unverified && <span className="admin-role-tag">Unverified (pending registration)</span>}
                        {l.disputeStatus === "open" && <span className="admin-role-tag">Disputed — see Supply Chain Disputes</span>}
                        {l.disputeStatus === "dismissed" && <span className="admin-role-tag">Dispute dismissed</span>}
                    </div>
                ))
            )}

            {suggestions.length > 0 && (
                <>
                    <h5 style={{ margin: "var(--space-3) 0 var(--space-1)" }}>CRM leads that may be this business</h5>
                    {suggestions.map((lead) => (
                        <div key={lead.id} className="admin-license-user-chip">
                            <span className="admin-license-user-name">{lead.company}</span>
                            <span className="admin-license-meta" style={{ display: "inline" }}>
                                {[lead.city, lead.state].filter(Boolean).join(", ")} · cited by {lead.activeCitationCount}
                            </span>
                            <span className="admin-role-tag">
                                {data.leadSuggestions.strong.some((s) => s.id === lead.id) ? "Same USDA # / EIN" : "Similar name"}
                            </span>
                            <button
                                type="button"
                                className="admin-action-btn"
                                disabled={linking === lead.id}
                                onClick={() => linkLead(lead)}
                            >
                                {linking === lead.id ? "Linking…" : "Link to this license"}
                            </button>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}
